import { SaSmsGuard } from '@components/sa-sms-messaging/sa-sms-guard';
import { SaSmsNav } from '@components/sa-sms-messaging/sa-sms-nav';
import { SmsApprovalsTable } from '@components/sa-sms-messaging/sms-approvals-table';
import { ISaSmsApprovalRow, ISaSmsPage } from '@interfaces/sa-sms';
import { useNavigation } from '@refinedev/core';
import { Alert, Button, Select, Space } from 'antd';
import { useSaSmsErrorText, useSaSmsGet } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Super-admin SMS console: pending limit-approval requests (approve / reject with a note). */
export const SaSmsApprovals: React.FC = () => {
    const { t } = useTranslation(['sa-sms']);
    const { push } = useNavigation();
    const errorText = useSaSmsErrorText();
    const [status, setStatus] = useState<string | undefined>('pending');
    const [page, setPage] = useState(1);
    const { data, isLoading, isError, error, refetch } = useSaSmsGet<
        Pick<ISaSmsPage<ISaSmsApprovalRow>, 'items' | 'page' | 'pageSize'>
    >('/approvals', { status, page });
    return (
        <SaSmsGuard>
            {(canEdit) => (
                <section className="sa-sms-page">
                    <SaSmsNav active="approvals" />
                    <Space style={{ marginBottom: 16 }}>
                        <Select
                            allowClear
                            style={{ width: 180 }}
                            placeholder={t('approvals.status')}
                            value={status}
                            onChange={(v) => {
                                setStatus(v);
                                setPage(1);
                            }}
                            options={['pending', 'approved', 'rejected', 'expired'].map((v) => ({
                                value: v,
                                label: t(`approvals.${v}`),
                            }))}
                        />
                    </Space>
                    {isError && (
                        <Alert
                            type="error"
                            showIcon
                            message={errorText(error)}
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    <SmsApprovalsTable
                        rows={data?.items ?? []}
                        loading={isLoading}
                        canEdit={canEdit}
                        onOpenTenant={(id) => push(`/sms-messaging/tenants/${id}`)}
                        onDone={() => refetch()}
                    />
                    <Space style={{ marginTop: 16 }}>
                        <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                            {t('actions.previous')}
                        </Button>
                        <span>{t('list.page', { page })}</span>
                        <Button
                            disabled={!data || data.items.length < data.pageSize}
                            onClick={() => setPage(page + 1)}
                        >
                            {t('actions.next')}
                        </Button>
                    </Space>
                </section>
            )}
        </SaSmsGuard>
    );
};
